import React, { useEffect, useMemo, useState } from 'react';
import { CONFIG } from './data/config';
import { Encabezado, LineaDistribuidor } from './components/Encabezado';
import { FUERA_DE_ZONA, PasoZona } from './components/PasoZona';
import { PasoProductos } from './components/PasoProductos';
import { PasoContacto } from './components/PasoContacto';
import { BarraTotal } from './components/BarraTotal';
import { ResumenLateral } from './components/ResumenLateral';
import { Confirmacion } from './components/Confirmacion';
import { FabWhatsApp } from './components/FabWhatsApp';
import { proximaEntrega, telLindo } from './utils/format';
import { armarPedido, registrarPedido, totalCarrito, unidadesCarrito, type Carrito } from './utils/pedido';
import type { DatosContacto, Pedido, Zona } from './types/pedido';
const DATOS_VACIOS: DatosContacto = {
  calle: '',
  piso: '',
  entre: '',
  nombre: '',
  tel: '',
  pago: 'Efectivo',
  notas: ''
};
interface AppProps {
  /** Paleta de la página: sigue al sistema, o se fuerza clara u oscura. */
  theme?: 'auto' | 'light' | 'dark';
  /** En demo el pedido no se envía a ningún lado y se avisa arriba. */
  modoDemo?: boolean;
}
export function App({
  theme = 'auto',
  modoDemo = true
}: AppProps) {
  const [valorBarrio, setValorBarrio] = useState('');
  const [carrito, setCarrito] = useState<Carrito>({});
  const [datos, setDatos] = useState<DatosContacto>(DATOS_VACIOS);
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const [enviando, setEnviando] = useState(false);
  useEffect(() => {
    const root = document.documentElement;
    if (theme !== 'auto') {
      root.setAttribute('data-theme', theme);
      return;
    }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const aplicar = () => root.setAttribute('data-theme', mq.matches ? 'dark' : 'light');
    aplicar();
    mq.addEventListener('change', aplicar);
    return () => mq.removeEventListener('change', aplicar);
  }, [theme]);
  const fueraDeZona = valorBarrio === FUERA_DE_ZONA;
  const zona: Zona | null = useMemo(() => fueraDeZona ? null : CONFIG.zonas.find(z => z.barrio === valorBarrio) ?? null, [valorBarrio, fueraDeZona]);
  const entrega = useMemo(() => zona ? proximaEntrega(zona.entregas, CONFIG.horaCorte) : null, [zona]);
  const total = totalCarrito(carrito);
  const unidades = unidadesCarrito(carrito);
  const puedeConfirmar = (Boolean(zona) || fueraDeZona) && datos.calle.trim() !== '' && datos.nombre.trim() !== '' && datos.tel.trim() !== '' && total > 0 && (CONFIG.pedidoMinimo === 0 || total >= CONFIG.pedidoMinimo);
  const onDato = (campo: keyof DatosContacto, valor: string) => setDatos(d => ({
    ...d,
    [campo]: valor
  }) as DatosContacto);
  const onCambio = (id: string, delta: number) => setCarrito(c => ({
    ...c,
    [id]: Math.max(0, (c[id] ?? 0) + delta)
  }));
  const confirmar = async () => {
    if (!puedeConfirmar) return;
    setEnviando(true);
    const nuevo = armarPedido({
      carrito,
      datos,
      barrio: fueraDeZona ? '(fuera de zona)' : zona?.barrio ?? '',
      entrega,
      rutaFallback: fueraDeZona ? 'NUEVA' : zona?.entregas[0]?.ruta ?? 'NUEVA'
    });
    if (!modoDemo) await registrarPedido(nuevo);
    setPedido(nuevo);
    setEnviando(false);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const reiniciar = () => {
    setPedido(null);
    setValorBarrio('');
    setCarrito({});
    setDatos(DATOS_VACIOS);
    window.scrollTo({
      top: 0
    });
  };
  return <main className="min-h-full w-full bg-ground" data-id="element-0" data-mp="1w3vlfyv4b72p">
      <div className="mx-auto max-w-shell px-[18px] lg:px-8" data-id="element-1" data-mp="1w2kg3um0xr9h">
        <Encabezado data-id="element-2" data-mp="1i2j71rvlvdix" />

        {pedido ? <Confirmacion pedido={pedido} entrega={entrega} onOtroPedido={reiniciar} data-id="element-3" data-mp="1i5nldi2sbpnb" /> : <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start lg:gap-8" data-id="element-4" data-mp="1m78z15n587yk">
            <div className="mx-auto max-w-col pb-44 lg:mx-0 lg:max-w-none lg:pb-16" data-id="element-5" data-mp="6qiee1vmf4xw">
              <LineaDistribuidor data-id="element-6" data-mp="arc14rxbudp7" />

              {modoDemo && <p className="mb-3.5 rounded-field bg-warn-soft px-3.5 py-2.5 text-[13px] leading-normal text-warn" data-id="element-7" data-mp="qy7493gpb59m">
                  <b className="font-bold" data-id="element-8" data-mp="m4ugswnrd4ig">Esto es la demo.</b> Podés tocar todo, pero el pedido no se envía a ningún
                  lado todavía. En la versión instalada te llega por WhatsApp y se guarda en tu planilla.
                </p>}

              <section className="pb-6 pt-1.5" data-id="element-9" data-mp="19vrlpph4z1k7">
                <h1 className="display text-[clamp(30px,8.5vw,40px)] lg:text-[42px]" data-id="element-10" data-mp="19vhtdv8mcibc">
                  Pedí tu agua y te la dejamos en la puerta
                </h1>
                <p className="mt-3 max-w-[44ch] text-[15.5px] text-ink-soft" data-id="element-11" data-mp="zx2agfjqwfcx">
                  Elegí tu barrio, mirá qué día pasamos por ahí y armá el pedido. Tarda menos de un minuto.
                </p>
              </section>

              <PasoZona valorBarrio={valorBarrio} zona={zona} fueraDeZona={fueraDeZona} entrega={entrega} datos={datos} onBarrio={setValorBarrio} onDato={onDato} data-id="element-12" data-mp="3kpm5u90cd1tr" />
              <PasoProductos carrito={carrito} total={total} onCambio={onCambio} data-id="element-13" data-mp="206timxlxzt9l" />
              <PasoContacto datos={datos} onDato={onDato} data-id="element-14" data-mp="254253ae4ijgn" />

              <p className="pb-1.5 pt-6 text-center text-[12.5px] leading-relaxed text-ink-faint" data-id="element-15" data-mp="29vtn2wj7rgv6">
                {CONFIG.negocio} · Entregamos de martes a viernes en Zona Norte.
                <br data-id="element-16" data-mp="2ex0tos9mea09" />
                Consultas al WhatsApp {telLindo(CONFIG.whatsapp)}
              </p>
            </div>

            <ResumenLateral carrito={carrito} total={total} zona={zona} fueraDeZona={fueraDeZona} entrega={entrega} puedeConfirmar={puedeConfirmar} enviando={enviando} onConfirmar={confirmar} data-id="element-17" data-mp="2nvum06fx4e80" />
          </div>}
      </div>

      {!pedido && <>
          <BarraTotal total={total} unidades={unidades} puedeConfirmar={puedeConfirmar} enviando={enviando} onConfirmar={confirmar} data-id="element-18" data-mp="21rkfto8la494" />
          <FabWhatsApp data-id="element-19" data-mp="2as8vsohjmeon" />
        </>}
    </main>;
}